/**
 * Client-local deterministic secret-area presentation for a generated dungeon.
 *
 * This module reads the plan but does not modify planner data or grant rewards.
 * Its single exported factory supplies the selected wall opening and builds the
 * concealed alcove from the existing interior kit.
 */

import * as THREE from 'three';

import type { DungeonPlan, DungeonRoom } from '@great-cloud/procedural-dungeons';

import type { AssetLoader } from '../assets/loader.js';
import { fullDetailAssetsActive } from '../scene/assetDetail.js';
import type { SecretCollisionFacts } from './interiorCollision.js';
import type { LayoutSecretFrame } from './layoutWalls.js';
import { DUNGEON_INTERIOR_SCALE, DUNGEON_SECRET_AREA_SCALE } from './interiorScale.js';

const SCALE = DUNGEON_INTERIOR_SCALE;
const SECRET = DUNGEON_SECRET_AREA_SCALE;
const BOUNDS_EPSILON = 0.01;

interface SecretSide {
  dx: number;
  dz: number;
  yaw: number;
}

interface MeasuredPiece {
  root: THREE.Object3D;
  size: THREE.Vector3;
}

const CARDINAL_SIDES: readonly SecretSide[] = [
  { dx: 1, dz: 0, yaw: -Math.PI / 2 },
  { dx: -1, dz: 0, yaw: Math.PI / 2 },
  { dx: 0, dz: 1, yaw: Math.PI },
  { dx: 0, dz: -1, yaw: 0 },
];

const DRESSING_VARIANTS = [
  ['stone-sarcophagus', 'bone-pile'],
  ['ritual-altar', 'brazier'],
  ['iron-cage', 'bone-pile'],
  ['rubble-pile', 'brazier'],
] as const;

/** Stable 32-bit FNV-1a over JavaScript UTF-16 code units. */
function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function pickByHash<T>(values: readonly T[], planId: string, salt: string): T {
  if (values.length === 0) {
    throw new Error(`Cannot choose dungeon secret ${salt} from an empty candidate list`);
  }
  return values[hashString(`${planId}\u0000${salt}`) % values.length]!;
}

function fallbackBox(width: number, height: number, depth: number, colour: number): THREE.Mesh {
  const geometry = new THREE.BoxGeometry(width, height, depth);
  geometry.translate(0, height / 2, 0);
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({ color: colour, roughness: 0.92 }),
  );
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function enableShadows(object: THREE.Object3D): void {
  object.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
  });
}

function groundAndMeasure(object: THREE.Object3D): MeasuredPiece | null {
  object.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(object);
  if (bounds.isEmpty()) return null;

  const size = bounds.getSize(new THREE.Vector3());
  const values = [
    bounds.min.x,
    bounds.min.y,
    bounds.min.z,
    bounds.max.x,
    bounds.max.y,
    bounds.max.z,
    size.x,
    size.y,
    size.z,
  ];
  if (values.some((value) => !Number.isFinite(value))) return null;
  if (Math.max(size.x, size.y, size.z) <= BOUNDS_EPSILON) return null;

  const centre = bounds.getCenter(new THREE.Vector3());
  object.position.x -= centre.x;
  object.position.y -= bounds.min.y;
  object.position.z -= centre.z;

  const root = new THREE.Group();
  root.add(object);
  enableShadows(root);

  return {
    root,
    size: new THREE.Vector3(
      Math.max(size.x, BOUNDS_EPSILON),
      Math.max(size.y, BOUNDS_EPSILON),
      Math.max(size.z, BOUNDS_EPSILON),
    ),
  };
}

async function loadMeasured(
  loader: AssetLoader | null,
  assetId: string,
  fallback: () => THREE.Object3D,
): Promise<MeasuredPiece> {
  const instance = loader
    ? await loader.instantiatePreferred(assetId, fullDetailAssetsActive()).catch(() => null)
    : null;
  const measured = groundAndMeasure(instance ?? fallback());
  if (measured) return measured;

  const fallbackMeasured = groundAndMeasure(fallback());
  if (!fallbackMeasured) {
    throw new Error(`Dungeon secret fallback for ${assetId} has no measurable geometry`);
  }
  return fallbackMeasured;
}

function fittedClone(
  piece: MeasuredPiece,
  width: number,
  height: number,
  depth: number,
): THREE.Object3D {
  const clone = piece.root.clone(true);
  clone.scale.set(width / piece.size.x, height / piece.size.y, depth / piece.size.z);
  return clone;
}

function tileWallSpan(
  parent: THREE.Object3D,
  wallPiece: MeasuredPiece,
  span: number,
  startX: number,
  namePrefix: string,
  height: number = SCALE.wallHeight,
): void {
  if (span <= BOUNDS_EPSILON) return;

  const nominalSpan = THREE.MathUtils.clamp(
    wallPiece.size.x,
    SCALE.minimumWallTileSpan,
    SCALE.maximumWallTileSpan,
  );
  const count = Math.max(1, Math.ceil(span / nominalSpan));
  const exactSpan = span / count;

  for (let index = 0; index < count; index += 1) {
    const tile = fittedClone(wallPiece, exactSpan, height, SCALE.wallThickness);
    tile.name = `${namePrefix}:tile-${index}`;
    tile.position.x = startX + exactSpan * (index + 0.5);
    parent.add(tile);
  }
}

function dressingFallback(assetId: string): THREE.Object3D {
  switch (assetId) {
    case 'stone-sarcophagus':
      return fallbackBox(1.5, 0.9, 0.8, 0x625e58);
    case 'ritual-altar':
      return fallbackBox(1.1, 1, 1.1, 0x665b4a);
    case 'iron-cage':
      return fallbackBox(1.05, 1.8, 1.05, 0x41464a);
    case 'brazier':
      return fallbackBox(0.7, 0.85, 0.7, 0x55493b);
    case 'bone-pile':
      return fallbackBox(0.9, 0.25, 0.7, 0x8b826d);
    case 'rubble-pile':
      return fallbackBox(0.9, 0.4, 0.75, 0x5d5a55);
    default:
      return fallbackBox(0.8, 0.8, 0.8, 0x625b50);
  }
}

function compareRoomIds(left: DungeonRoom, right: DungeonRoom): number {
  if (left.id < right.id) return -1;
  if (left.id > right.id) return 1;
  return 0;
}

/**
 * Where the secret sits and how big it is, in the v1 assembly's frame: origin on
 * the host wall line at the entrance centre, local +x along the wall, local −z
 * outward into the alcove. The v1 grid path and the schema-v2 layout path
 * (`layoutWalls.ts` `LayoutSecretFrame`) both reduce to this, so one assembly
 * builds either.
 */
interface SecretAssemblyFrame {
  originX: number;
  originZ: number;
  yaw: number;
  panelOnNegativeSide: boolean;
  entranceClearWidth: number;
  panelWidth: number;
  alcoveWidth: number;
  alcoveDepth: number;
  wallHeight: number;
}

function secretBounds(frame: SecretAssemblyFrame): {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
} {
  const outwardExtent = frame.alcoveDepth + SCALE.wallThickness + SCALE.wallThickness / 2;
  const lateralHalf = frame.alcoveWidth / 2;
  const cos = Math.cos(frame.yaw);
  const sin = Math.sin(frame.yaw);
  const xs: number[] = [];
  const zs: number[] = [];
  for (const [lx, lz] of [
    [-lateralHalf, 0],
    [lateralHalf, 0],
    [-lateralHalf, -outwardExtent],
    [lateralHalf, -outwardExtent],
  ] as const) {
    xs.push(frame.originX + lx * cos + lz * sin);
    zs.push(frame.originZ - lx * sin + lz * cos);
  }
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minZ: Math.min(...zs),
    maxZ: Math.max(...zs),
  };
}

async function buildSecretGroup(
  planId: string,
  hostRoomId: string,
  frame: SecretAssemblyFrame,
  loader: AssetLoader | null,
): Promise<THREE.Group> {
  const wallHeight = frame.wallHeight;
  const secret = new THREE.Group();
  secret.name = `secret:${planId}:host:${hostRoomId}`;
  secret.position.set(frame.originX, 0, frame.originZ);
  secret.rotation.y = frame.yaw;

  const [floorPiece, wallPiece, thresholdPiece, rubblePiece] = await Promise.all([
    loadMeasured(loader, 'floor-tile', () =>
      fallbackBox(
        SCALE.maximumWallTileSpan,
        SCALE.floorThickness,
        SCALE.maximumWallTileSpan,
        0x40464b,
      ),
    ),
    loadMeasured(loader, 'wall-straight', () =>
      fallbackBox(SCALE.maximumWallTileSpan, wallHeight, SCALE.wallThickness, 0x4d5357),
    ),
    loadMeasured(loader, 'threshold', () =>
      fallbackBox(
        frame.entranceClearWidth,
        SCALE.thresholdHeight,
        SCALE.wallThickness / 2,
        0x595149,
      ),
    ),
    loadMeasured(loader, 'rubble-pile', () => dressingFallback('rubble-pile')),
  ]);

  const clearWidth = frame.alcoveWidth - SCALE.wallThickness * 2;
  const depth = frame.alcoveDepth;
  const floor = fittedClone(floorPiece, clearWidth, SCALE.floorThickness, depth);
  floor.name = `${secret.name}:floor`;
  floor.position.set(0, -SCALE.floorThickness, -(SCALE.wallThickness / 2 + depth / 2));
  secret.add(floor);

  const ceiling = fittedClone(floorPiece, clearWidth, SCALE.ceilingThickness, depth);
  ceiling.name = `${secret.name}:ceiling`;
  ceiling.position.set(0, wallHeight, -(SCALE.wallThickness / 2 + depth / 2));
  ceiling.rotation.x = Math.PI;
  secret.add(ceiling);

  for (const sign of [-1, 1] as const) {
    const sideWall = new THREE.Group();
    sideWall.name = `${secret.name}:side-wall:${sign}`;
    sideWall.position.set(
      sign * (frame.alcoveWidth / 2 - SCALE.wallThickness / 2),
      0,
      -(SCALE.wallThickness / 2 + depth / 2),
    );
    sideWall.rotation.y = Math.PI / 2;
    tileWallSpan(sideWall, wallPiece, depth, -depth / 2, sideWall.name, wallHeight);
    secret.add(sideWall);
  }

  const backWall = new THREE.Group();
  backWall.name = `${secret.name}:back-wall`;
  backWall.position.z = -(depth + SCALE.wallThickness);
  tileWallSpan(backWall, wallPiece, clearWidth, -clearWidth / 2, backWall.name, wallHeight);
  secret.add(backWall);

  for (const sign of [-1, 1] as const) {
    const jamb = fittedClone(wallPiece, SCALE.wallThickness, wallHeight, SCALE.wallThickness);
    jamb.name = `${secret.name}:entrance-jamb:${sign}`;
    jamb.position.x = sign * (frame.entranceClearWidth / 2 + SCALE.wallThickness / 2);
    secret.add(jamb);
  }

  const threshold = fittedClone(
    thresholdPiece,
    frame.entranceClearWidth,
    SCALE.thresholdHeight,
    SCALE.wallThickness / 2,
  );
  threshold.name = `${secret.name}:threshold`;
  threshold.position.z = -SCALE.wallThickness / 4;
  secret.add(threshold);

  const halfSpan = frame.entranceClearWidth / 2;
  const gapWidth = frame.entranceClearWidth - frame.panelWidth;
  const panelCentreX = frame.panelOnNegativeSide
    ? -halfSpan + frame.panelWidth / 2
    : halfSpan - frame.panelWidth / 2;
  const gapCentreX = frame.panelOnNegativeSide ? halfSpan - gapWidth / 2 : -halfSpan + gapWidth / 2;
  const panel = fittedClone(
    wallPiece,
    frame.panelWidth,
    wallHeight - SCALE.thresholdHeight,
    SCALE.wallThickness,
  );
  panel.name = `${secret.name}:false-wall-panel`;
  panel.position.set(
    panelCentreX,
    SCALE.thresholdHeight,
    -(SCALE.wallThickness / 2 + SECRET.secretPanelRecess),
  );
  secret.add(panel);

  const rubbleNaturalFootprint = Math.max(rubblePiece.size.x, rubblePiece.size.z);
  const rubbleScale = Math.min(
    1,
    SECRET.secretRubbleMaximumFootprint / rubbleNaturalFootprint,
    (wallHeight - SECRET.secretDressingClearance) / rubblePiece.size.y,
  );
  const rubbleRadius = (rubbleNaturalFootprint * rubbleScale) / 2;
  const rubble = rubblePiece.root.clone(true);
  rubble.name = `${secret.name}:concealing-rubble`;
  rubble.scale.setScalar(rubbleScale);
  rubble.position.set(
    gapCentreX,
    0,
    -(
      SCALE.wallThickness +
      SECRET.secretPanelRecess +
      SECRET.secretDressingClearance +
      rubbleRadius
    ),
  );
  rubble.rotation.y = (hashString(`${planId}\u0000secret-rubble-rotation`) % 8) * (Math.PI / 4);
  secret.add(rubble);

  const dressingIds = pickByHash(DRESSING_VARIANTS, planId, 'secret-dressing-variant');
  const dressingPieces = await Promise.all(
    dressingIds.map((assetId) => loadMeasured(loader, assetId, () => dressingFallback(assetId))),
  );
  const lateralOffset = Math.max(
    0,
    clearWidth / 2 - SECRET.secretDressingMaximumFootprint / 2 - SECRET.secretDressingClearance,
  );
  const dressingZ =
    -(SCALE.wallThickness / 2 + depth) +
    SECRET.secretDressingClearance +
    SECRET.secretDressingMaximumFootprint / 2;

  for (const [index, assetId] of dressingIds.entries()) {
    const piece = dressingPieces[index]!;
    const naturalFootprint = Math.max(piece.size.x, piece.size.z);
    const propScale = Math.min(
      1,
      SECRET.secretDressingMaximumFootprint / naturalFootprint,
      (wallHeight - SECRET.secretDressingClearance) / piece.size.y,
    );
    const prop = piece.root.clone(true);
    prop.name = `${secret.name}:dressing:${assetId}:${index}`;
    prop.scale.setScalar(propScale);
    prop.position.set(index === 0 ? -lateralOffset : lateralOffset, 0, dressingZ);
    prop.rotation.y =
      (hashString(`${planId}\u0000secret-dressing-rotation:${index}`) % 4) * (Math.PI / 2);
    secret.add(prop);
  }

  const lightColours = [0x6f87a8, 0x8070a0, 0x668d83] as const;
  const lightIntensities = [0.28, 0.34, 0.4] as const;
  const light = new THREE.PointLight(
    pickByHash(lightColours, planId, 'secret-light-colour'),
    pickByHash(lightIntensities, planId, 'secret-light-intensity'),
    frame.alcoveWidth * 1.15,
    2,
  );
  light.name = `${secret.name}:light`;
  light.position.set(0, wallHeight * 0.38, dressingZ);
  secret.add(light);

  return secret;
}

/**
 * The schema-v2 secret (AD-404 §4.5): the host wall, gap and alcove were chosen by
 * the generator's layout stage, so nothing is picked here — the assembly only
 * dresses the frame `planLayoutWalls` derived from `plan.layout.secret`.
 */
export function createLayoutSecretArea(
  planId: string,
  frame: LayoutSecretFrame,
  loader: AssetLoader | null,
): {
  readonly bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
  build: () => Promise<THREE.Group>;
} {
  const assembly: SecretAssemblyFrame = { ...frame, wallHeight: frame.height };
  return {
    bounds: secretBounds(assembly),
    build: () => buildSecretGroup(planId, frame.hostRoomId, assembly, loader),
  };
}

/**
 * Select and assemble exactly one visual secret for a **v1 (schema-1) plan** — the
 * one-release fallback (AD-404 §8). The returned coordinates are local to the
 * dungeon interior group.
 */
export function createSecretArea(
  plan: DungeonPlan,
  loader: AssetLoader | null,
  spacing: number,
): {
  readonly entranceSpan: number;
  readonly bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
  /** The hash-chosen facts the collision derivation needs; see `interiorCollision.ts`. */
  readonly collisionFacts: SecretCollisionFacts;
  isEntrance: (room: DungeonRoom, side: { dx: number; dz: number }) => boolean;
  build: () => Promise<THREE.Group>;
} {
  const connected = new Set<string>();
  for (const connection of plan.connections) {
    connected.add(`${connection.a}->${connection.b}`);
    connected.add(`${connection.b}->${connection.a}`);
  }

  const roomByCoordinate = new Map(
    plan.rooms.map((room) => [`${room.coordinate.x},${room.coordinate.z}`, room]),
  );

  const closedSides = (room: DungeonRoom): readonly SecretSide[] =>
    CARDINAL_SIDES.filter((side) => {
      const neighbour = roomByCoordinate.get(
        `${room.coordinate.x + side.dx},${room.coordinate.z + side.dz}`,
      );
      return neighbour === undefined || !connected.has(`${room.id}->${neighbour.id}`);
    });

  const eligibleRooms = plan.rooms
    .filter((room) => room.encounter?.kind !== 'boss' && closedSides(room).length > 0)
    .slice()
    .sort(compareRoomIds);
  const ordinaryRooms = eligibleRooms.filter(
    (room) => room.role !== 'entrance' && room.role !== 'exit',
  );
  const hostPool = ordinaryRooms.length > 0 ? ordinaryRooms : eligibleRooms;

  if (hostPool.length === 0) {
    throw new Error(`Dungeon ${plan.id} has no non-boss closed wall for its required secret area`);
  }

  const host = pickByHash(hostPool, plan.id, 'secret-host-room');
  const hostClosedSides = closedSides(host);
  const vacantSides = hostClosedSides.filter(
    (side) =>
      !roomByCoordinate.has(`${host.coordinate.x + side.dx},${host.coordinate.z + side.dz}`),
  );
  const side = pickByHash(
    vacantSides.length > 0 ? vacantSides : hostClosedSides,
    plan.id,
    'secret-host-side',
  );

  // Hoisted out of build() so the collision derivation reads the SAME hash choice
  // the geometry does; re-deriving it there would be a second implementation of one
  // decision, and the two could disagree about which half of the entrance is solid.
  const panelOnNegativeSide = (hashString(`${plan.id}\u0000secret-panel-side`) & 1) === 0;
  const frame: SecretAssemblyFrame = {
    originX: host.coordinate.x * spacing + side.dx * (SCALE.roomSize / 2),
    originZ: host.coordinate.z * spacing + side.dz * (SCALE.roomSize / 2),
    yaw: side.yaw,
    panelOnNegativeSide,
    entranceClearWidth: SECRET.secretEntranceClearWidth,
    panelWidth: SECRET.secretPanelWidth,
    alcoveWidth: SECRET.secretAlcoveWidth,
    alcoveDepth: SECRET.secretAlcoveDepth,
    wallHeight: SCALE.wallHeight,
  };

  return {
    entranceSpan: SECRET.secretEntranceOuterWidth,
    bounds: secretBounds(frame),
    collisionFacts: { hostRoomId: host.id, side, panelOnNegativeSide },
    isEntrance: (room, candidateSide) =>
      room.id === host.id && candidateSide.dx === side.dx && candidateSide.dz === side.dz,
    build: () => buildSecretGroup(plan.id, host.id, frame, loader),
  };
}
